/* SpawnAnything — "Items": one window with every tool of the battle tool belt as a card with a rotating 3D model
   (Werkzeuge, Katastrophen, Aus Kisten) and the hotbar on top: nine slots for the number keys 1–9. Pick a card, then
   a slot (or drag a card / a slot onto a slot); ✕ empties a slot, "Standard" restores the classic belt. The hotbar
   (src/game/hotbar.js, localStorage 'aw-hotbar') drives the battle belt (#tools) and the digit keys:
   - the belt re-orders its EXISTING [data-tool] buttons (main.js and the features bound per-button listeners to them;
     moving a node keeps them) and rewrites their <b> key labels; tools not on the bar are hidden;
   - main.js's TOOL_KEYS array (digit key n → TOOL_KEYS[n - 1]) is updated in place;
   - fixed extras stay as before: the hand ("Übernehmen", the base mode every reset returns to) is always shown,
     H always picks the grab hand, 0 always cycles the disasters (the group keeps the disasters that are not on the bar).
   Locked crate tools may sit in a slot; they stay unusable (setTool opens the crate screen, as before). */
import './itemWindow.css';
import { createWindow, card } from './window.js';
import { getPreview3D } from './preview3d.js';
import { itemObject } from './itemModels.js';
import { createHotbar, SLOTS } from '../game/hotbar.js';
import { gateOf, RARITY } from '../game/unlocks.js';
import { ERA_TOOLS } from '../content/eras.js';
import { DISASTER_TOOLS } from '../features/disaster.js';

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const DT = DISASTER_TOOLS;
/** catalogue: key → { name, text, group } (group: 'tool' | 'disaster' | 'crate') */
const ITEMS = {
  hand: { name: 'Übernehmen', text: 'Einheit anklicken und selbst steuern' },
  grab: { name: 'Gotteshand', text: 'Einheiten packen und werfen' },
  granate: { name: 'Granate', text: 'Kleine Explosion' },
  fass: { name: 'Sprengfass', text: 'Große Explosion, wirft alles um' },
  meteor: { name: 'Meteorit', text: ERA_TOOLS.meteor?.desc || '' },
  katapult: { name: 'Katapultstein', text: ERA_TOOLS.katapult?.desc || '' },
  artillerie: { name: 'Artilleriesalve', text: ERA_TOOLS.artillerie?.desc || '' },
  stinkwolke: { name: 'Stinkwolke', text: ERA_TOOLS.stinkwolke?.desc || '' },
  ...Object.fromEntries(Object.entries(DT).map(([k, d]) => [k, { name: d.name, text: d.hint, group: 'disaster' }])),
  atom: { name: 'Atombombe', text: 'Riesige Druckwelle mit Atompilz' },
  orbitallaser: { name: 'Orbitallaser', text: ERA_TOOLS.orbitallaser?.desc || '' },
};
for (const [k, it] of Object.entries(ITEMS)) it.group = it.group || (gateOf('tool', k) ? 'crate' : 'tool');
const GROUPS = [['tool', 'Werkzeuge', 'ti-hammer'], ['disaster', 'Katastrophen', 'ti-tornado'], ['crate', 'Aus Kisten', 'ti-box']];

/**
 * deps: { toolKeys: main.js TOOL_KEYS (updated in place), shop ({ locked(kind, key), inv.subscribe }), toast(msg),
 *         pause() → resume fn | null (battle: the window pauses while open) }
 */
export function createItemWindow({ toolKeys = null, shop = null, toast = () => {}, pause = () => null } = {}) {
  const belt = document.getElementById('tools');
  const btnOf = k => belt?.querySelector(`[data-tool="${k}"]`) || null;
  // only items whose belt button exists (a feature switched off = not offered)
  const known = k => !!ITEMS[k] && (!belt || !!btnOf(k));
  const hb = createHotbar({ isKnown: known });
  const isLocked = k => !!shop?.locked?.('tool', k);
  const pv = getPreview3D();

  /* ---------------- window ---------------- */
  const W = createWindow({ id: 'itemWin', title: 'Items', sub: 'Deine Schnellleiste für den Kampf', icon: 'ti-backpack', wide: true,
    foot: 'Im Kampf wählen die Tasten 1–9 das Item im Platz. H = Gotteshand, 0 = Katastrophen durchschalten.' });
  // the hotbar sits between head and scrolling body, so it stays in view while the cards scroll
  const barSec = document.createElement('section'); barSec.className = 'aw-win-sec iw-bar-sec';
  W.box.insertBefore(barSec, W.body);
  barSec.innerHTML = `<h3><i class="ti ti-keyboard" aria-hidden="true"></i>Schnellleiste<span class="iw-hint">Karte wählen, dann einen Platz anklicken – oder hinziehen</span><button class="ghost iw-reset" type="button" title="Die ursprüngliche Belegung">Standard</button></h3><div class="iw-bar" role="list"></div>`;
  const barEl = barSec.querySelector('.iw-bar'), hintEl = barSec.querySelector('.iw-hint');
  const HINT = hintEl.textContent;
  const slots = [];
  for (let i = 0; i < SLOTS; i++) {
    const s = document.createElement('div');
    s.className = 'iw-slot'; s.dataset.slot = i; s.setAttribute('role', 'listitem');
    // key badge and ✕ sit in a head row above the 3D view (the preview canvas paints over anything inside the view)
    s.innerHTML = `<div class="iw-slot-head"><b class="iw-key">${i + 1}</b><button class="iw-slot-x" type="button" aria-label="Platz ${i + 1} leeren" title="Platz leeren">✕</button></div><button class="iw-slot-b" type="button" draggable="true"><span class="iw-slot-view"></span><span class="iw-slot-t"></span></button>`;
    barEl.appendChild(s); slots.push(s);
  }
  const cards = new Map(); // key -> card element
  for (const [g, title, icon] of GROUPS) {
    const keys = Object.keys(ITEMS).filter(k => ITEMS[k].group === g && known(k));
    if (!keys.length && g !== 'crate') continue;
    const sec = document.createElement('section'); sec.className = 'aw-win-sec';
    sec.innerHTML = `<h3><i class="ti ${icon}" aria-hidden="true"></i>${esc(title)}</h3><div class="aw-grid"></div>`;
    const grid = sec.querySelector('.aw-grid');
    for (const k of keys) {
      const c = card({ title: ITEMS[k].name, text: ITEMS[k].text, cls: 'iw-card' });
      c.dataset.item = k; c.draggable = true; c.title = ITEMS[k].text;
      grid.appendChild(c); cards.set(k, c);
      pv.add(c.querySelector('.aw-card-view'), () => itemObject(k), { clip: W.body });
    }
    if (g === 'crate' && belt?.querySelector('[data-crate="boss"]')) {
      const c = card({ title: 'Bosse', text: 'Öffnet die Kisten', cls: 'iw-card iw-boss' });
      c.title = 'Bosse gibt es nur in Kisten'; grid.appendChild(c);
      pv.add(c.querySelector('.aw-card-view'), () => itemObject('boss'), { clip: W.body });
      c.addEventListener('click', () => { W.close(); belt.querySelector('[data-crate="boss"]')?.click(); });
    }
    W.body.appendChild(sec);
  }

  /* ---------------- picking: card → slot, slot → slot, drag & drop ---------------- */
  let sel = null; // selected item key (from a card or a slot)
  const select = k => {
    sel = sel === k ? null : k;
    for (const [kk, c] of cards) c.setAttribute('aria-pressed', String(kk === sel));
    barEl.classList.toggle('picking', !!sel);
    hintEl.textContent = sel ? `${ITEMS[sel].name}: jetzt einen Platz 1–9 anklicken` : HINT;
  };
  for (const [k, c] of cards) {
    c.addEventListener('click', () => select(k));
    c.addEventListener('dragstart', e => { e.dataTransfer.setData('text/plain', 'item:' + k); e.dataTransfer.effectAllowed = 'copyMove'; });
  }
  slots.forEach((s, i) => {
    const b = s.querySelector('.iw-slot-b');
    b.addEventListener('click', () => {
      const cur = hb.get()[i];
      if (sel) { hb.assign(i, sel); select(null); } // a key already on the bar swaps places
      else if (cur) select(cur); // pick it up: the next slot click moves it
    });
    s.querySelector('.iw-slot-x').addEventListener('click', () => { hb.clear(i); if (sel) select(null); });
    b.addEventListener('dragstart', e => { const cur = hb.get()[i]; if (!cur) { e.preventDefault(); return; } e.dataTransfer.setData('text/plain', 'slot:' + i); e.dataTransfer.effectAllowed = 'move'; });
    s.addEventListener('dragover', e => { e.preventDefault(); s.classList.add('over'); });
    s.addEventListener('dragleave', () => s.classList.remove('over'));
    s.addEventListener('drop', e => {
      e.preventDefault(); s.classList.remove('over');
      const d = e.dataTransfer.getData('text/plain') || '';
      if (d.startsWith('item:')) hb.assign(i, d.slice(5));
      else if (d.startsWith('slot:')) hb.swap(+d.slice(5), i);
      if (sel) select(null);
    });
  });
  barSec.querySelector('.iw-reset').addEventListener('click', () => { hb.reset(); if (sel) select(null); toast('Schnellleiste: Standard-Belegung'); });

  /* ---------------- paint: slots, card tags, lock state ---------------- */
  const shown = new Array(SLOTS).fill(undefined);
  function paint() {
    const bar = hb.get();
    slots.forEach((s, i) => {
      const k = bar[i], view = s.querySelector('.iw-slot-view');
      s.classList.toggle('empty', !k); s.classList.toggle('locked', !!k && isLocked(k));
      s.querySelector('.iw-slot-t').textContent = k ? ITEMS[k].name : 'leer';
      s.querySelector('.iw-slot-x').style.visibility = k ? '' : 'hidden';
      s.querySelector('.iw-slot-b').title = k ? `Taste ${i + 1}: ${ITEMS[k].name}${isLocked(k) ? ' (gibt es in Kisten)' : ''}` : `Taste ${i + 1}: leer`;
      if (shown[i] !== k) { shown[i] = k; if (k) pv.add(view, () => itemObject(k), { clip: barEl, spin: 0.4 }); else pv.remove(view); }
    });
    for (const [k, c] of cards) {
      const i = bar.indexOf(k), lock = isLocked(k), g = gateOf('tool', k);
      c.classList.toggle('locked', lock); c.classList.toggle('on-bar', i >= 0);
      const t = c.querySelector('.aw-card-t');
      const tag = i >= 0 ? `Taste ${i + 1}` : k === 'grab' ? 'Taste H' : DT[k] ? 'Taste 0' : '';
      t.innerHTML = `${esc(ITEMS[k].name)}${tag ? ` <em>${esc(tag)}</em>` : ''}`;
      let s = c.querySelector('.aw-card-s'); if (!s) { s = document.createElement('span'); s.className = 'aw-card-s'; c.appendChild(s); }
      s.textContent = lock ? `In Kisten (${RARITY[g.rarity].name})` : ITEMS[k].text;
      if (g) c.style.setProperty('--rc', RARITY[g.rarity].color);
    }
  }

  /* ---------------- the battle belt follows the bar ---------------- */
  const keyLabel = (btn, txt) => {
    let b = btn.querySelector(':scope > b');
    if (txt == null) { b?.remove(); return; }
    if (!b) { b = document.createElement('b'); btn.prepend(b); }
    b.textContent = txt;
  };
  function applyBelt() {
    const bar = hb.get();
    if (toolKeys) { toolKeys.length = 0; toolKeys.push(...bar); }
    if (!belt) return;
    const ths = [...belt.querySelectorAll(':scope > .th')], grp = belt.querySelector(':scope > .disaster-grp'), fly = grp?.querySelector('.disaster-fly');
    const order = [];
    if (ths[0]) order.push(ths[0]);
    const hand = btnOf('hand');
    if (hand && !bar.includes('hand')) { keyLabel(hand, null); hand.hidden = false; order.push(hand); } // the base mode stays reachable
    bar.forEach((k, i) => {
      const b = k && btnOf(k); if (!b) return;
      keyLabel(b, String(i + 1)); b.hidden = false; b.dataset.slot = i; order.push(b);
      if (k === 'grab') b.title = 'Gotteshand (auch Taste H): Einheiten packen und werfen. Alt: Pulk packen. Mausrad oder Shift: Höhe.';
      if (DT[k]) b.title = `${DT[k].name}: ${DT[k].hint}`;
    });
    for (const k of Object.keys(ITEMS)) {
      if (bar.includes(k) || k === 'hand') continue;
      const b = btnOf(k); if (!b) continue;
      delete b.dataset.slot;
      if (k === 'grab') { keyLabel(b, 'H'); b.hidden = false; b.title = 'Einheiten packen und werfen. Alt: Pulk packen. Mausrad oder Shift: Höhe.'; order.push(b); }
      else if (DT[k]) { keyLabel(b, null); b.hidden = false; b.removeAttribute('title'); fly?.appendChild(b); } // back into the group (key 0)
      else b.hidden = true; // not on the bar: off the belt
    }
    if (fly) { for (const k of Object.keys(DT)) { const b = fly.querySelector(`[data-tool="${k}"]`); if (b) fly.appendChild(b); } grp.hidden = !fly.querySelector('[data-tool]'); }
    if (grp) { if (ths[1]) { ths[1].hidden = grp.hidden; order.push(ths[1]); } order.push(grp); }
    if (ths[2]) order.push(ths[2]);
    for (const el of belt.children) if (!order.includes(el) && !el.matches('.iw-open')) order.push(el); // boss crate, placeholders
    if (dockBtn) order.push(dockBtn);
    for (const el of order) belt.appendChild(el); // moving keeps every listener
  }

  /* ---------------- entry points ---------------- */
  let resume = null;
  W.onOpen(() => { paint(); resume = pause(); });
  W.onClose(() => { if (sel) select(null); const r = resume; resume = null; r?.(); });
  // setup: the "Items" tile of the setup hub (main.js); battle: the button in the bottom bar below
  let dockBtn = null;
  if (belt) {
    dockBtn = document.createElement('button');
    dockBtn.className = 'iw-open'; dockBtn.type = 'button'; dockBtn.title = 'Items und Schnellleiste (pausiert die Schlacht)';
    dockBtn.innerHTML = '<i class="ti ti-backpack" aria-hidden="true"></i>Items';
    dockBtn.addEventListener('click', () => W.open());
    belt.appendChild(dockBtn);
  }

  hb.subscribe(() => { applyBelt(); if (W.isOpen) paint(); });
  shop?.inv?.subscribe?.(() => { if (W.isOpen) paint(); });
  applyBelt(); paint();
  return { open: () => W.open(), close: () => W.close(), hotbar: hb };
}
